<template>
  <div>
    <vx-card class="mt-base mb-5" :title="activityTitle">
      <p class="mb-5">
        Note: do not use a mercury thermometer during this experiment. Please see this resource on
        how to identify a mercury thermometer.
      </p>
      <p class="mb-5">
        Vital signs are the basic measurements of bodily function that medical professionals use to
        help measure someone’s health. They can give clues to the type and severity of someone’s
        condition, as well as help mark their recovery.
      </p>
      <p class="mb-5">The primary four vital signs are:</p>

      <ul class="default-list">
        <li>Temperature</li>
        <li>Pulse</li>
        <li>Respiratory Rate</li>
        <li>Blood Pressure</li>
      </ul>

      <p class="mb-5">
        You need a special machine to measure blood pressure, so we won’t do that now. Today your
        task is to:
      </p>

      <ul class="default-list">
        <li>
          Measure your own vital signs three times during the day, taking note of your age and
          gender.
        </li>
        <li>
          If you have someone willing to help you out, it might be interesting to measure their
          vital signs and compare the two! Make sure they are OK with you writing down their details
          (you don’t need to include their names).
        </li>
      </ul>

      <h4 class="mb-5">Temperature</h4>
      <p class="mb-5">
        The main reason for checking body temperature is to detect an inflammation or a fever, which
        is usually considered a temperature of 37.8°C or higher.
      </p>
      <p class="mb-5">
        Temperature is usually measured using a digital thermometer placed in the mouth, rectum,
        armpit, or the ear and the skin if you have devices made to measure temperature from those
        two sources.
      </p>
      <p class="mb-5">To use a digital thermometer:</p>

      <ul class="default-list">
        <li>Clean the tip with cold water and soap, then rinse it.</li>
        <li>Turn the thermometer on.</li>
        <li>Put the tip under your tongue, towards the back of your mouth.</li>
        <li>Close your lips around the thermometer.</li>
        <li>Wait until it beeps or flashes.</li>
        <li>Check the temperature on the display.</li>
      </ul>

      <h4 class="mb-5">Pulse</h4>
      <p class="mb-5">
        Checking a patient’s pulse is a quick indicator of areas such as heart function, stress,
        physical activity. In short, it’s to see how well and how hard your heart is working.
      </p>
      <p class="mb-5">
        You might check your pulse during or after exercise to see how quickly your heart recovers
        from activity, just during the day, or after you wake up but before you get out of bed,
        which is called your resting pulse or heart rate.
      </p>
      <p class="mb-5">Most adults have a resting heart rate between 60 and 100bpm.</p>
      <p class="mb-5">
        The fitter you are, the lower your resting heart rate is likely to be. For example, athletes
        may have a resting heart rate of 40 to 60bpm, or lower.
      </p>

      <h4 class="mb-5">Finding your pulse</h4>
      <p class="mb-5">You can find your pulse in your wrist or neck.</p>

      <p class="mb-5">To find your pulse in your wrist:</p>

      <ul class="default-list">
        <li>hold out one of your hands, with your palm facing upwards</li>
        <li>
          press the first (index) finger and middle finger of your other hand on the inside of your
          wrist, at the base of your thumb – don't use your thumb as it has its own pulse
        </li>
        <li>
          press your skin lightly until you can feel your pulse – if you can't find it, try pressing
          a little harder or move your fingers around
        </li>
      </ul>

      <p class="mb-5">To find your pulse in your neck:</p>

      <ul class="default-list">
        <li>
          press your first finger and middle finger to the side of your neck, just under your jaw
          and beside your windpipe – don't use your thumb
        </li>
        <li>
          press your skin lightly to feel your pulse – if you can't find it, try pressing a bit
          harder or move your fingers around
        </li>
      </ul>

      <h4 class="mb-5">Checking your pulse</h4>

      <p class="mb-5">When you find your pulse, either:</p>

      <ul class="default-list">
        <li>count the number of beats you feel for 60 seconds</li>
        <li>count the number for 30 seconds and multiply by 2</li>
      </ul>

      <p class="mb-5">
        It’s better to not look at a clock while you do this, but to use a timer so that you don’t
        lose count or get confused.
      </p>
      <p class="mb-5">
        This gives you your heart rate – the number of times your heart beats per minute (bpm).
      </p>
      <p class="mb-5">
        You can also check if your pulse is regular or irregular by feeling its rhythm for about 30
        seconds. It's very common to have occasional irregular heartbeats, such as missed beats.
      </p>
      <p class="mb-5">For this part of the task, measure your heart rate three times.</p>

      <ul class="default-list">
        <li>Measure your resting heart rate</li>
        <li>Measure your heart rate when you haven’t been exerting yourself during the day</li>
        <li>Measure your heart rate after moderate exercise</li>
      </ul>

      <h4 class="mb-5">Breathing Rate</h4>
      <p class="mb-5">
        Having an abnormal respiratory rate can indicate a variety of things. In some cases, a high
        or low respiratory rate is due to an activity, such as exercise, and is not an indication
        that there is anything wrong.
      </p>
      <p class="mb-5">
        Sometimes, however, various diseases, injuries, and substances can lead to a change in
        breathing. In a medical setting, an abnormal respiratory rate, especially if it is too fast,
        can indicate a health problem.
      </p>
      <p class="mb-5">To find your breathing rate,</p>
      <p class="mb-5">
        Count the number of breaths you feel for 60 seconds, or count the number for 30 seconds and
        multiply by 2.
      </p>
      <p class="mb-5">
        It’s better to not look at a clock while you do this, but to use a timer so that you don’t
        lose count or get confused.
      </p>
      <p class="mb-5">
        This gives you your heart rate – the number of times your heart beats per minute (bpm).
      </p>
      <p class="mb-5">
        You can also check if your pulse is regular or irregular by feeling its rhythm for about 30
        seconds. It's very common to have occasional irregular heartbeats, such as missed beats.
      </p>
      <p class="mb-5">For this part of the task, measure your heart rate three times.</p>

      <ul class="default-list">
        <li>Measure your resting respiratory rate</li>
        <li>Measure your breathing rate when you haven’t been exerting yourself during the day</li>
        <li>Measure your breathing rate after moderate exercise.</li>
      </ul>

      <p class="mb-5 text-bold">Resting Respiratory Rate:</p>
      <text-block v-model="data.question1" :editable="isEditable" />

      <p class="mb-5 text-bold">Breathing Rate:</p>
      <text-block v-model="data.question2" :editable="isEditable" />

      <p class="mb-5 text-bold">Breathing Rate after exercise:</p>
      <text-block v-model="data.question3" :editable="isEditable" />

      <save-buttons :is-editable="isEditable" @save="save" />
    </vx-card>
  </div>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  methods: methods,
  computed: computed,
  inject: inject,
};
</script>
